<script lang="ts">
  import { authState } from '../../lib/auth.svelte';
  import NavIcon from './NavIcon.svelte';

  type NavItem = 'overview' | 'subscription' | 'businesses' | 'team' | 'business-invitations' | 'integrations' | 'credit-application' | 'support';

  interface NavItemConfig {
    id: NavItem;
    label: string;
    icon: string;
    show: boolean;
  }

  interface Props {
    activeNav: NavItem;
    navItems: NavItemConfig[];
    onNavigate: (nav: NavItem) => void;
    onLogout: () => void;
  }

  let { activeNav, navItems, onNavigate, onLogout }: Props = $props();

  // Sidebar state
  let collapsed = $state(false);
  let mobileOpen = $state(false);

  function handleNavClick(id: NavItem) {
    onNavigate(id);
    mobileOpen = false;
  }

  export function toggleMobile() {
    mobileOpen = !mobileOpen;
  }

  export function closeMobile() {
    mobileOpen = false;
  }
</script>

<!-- Mobile Overlay -->
{#if mobileOpen}
  <div class="mobile-sidebar-overlay" onclick={() => mobileOpen = false}></div>
{/if}

<!-- Sidebar -->
<aside class="sidebar" class:collapsed class:mobile-open={mobileOpen}>
  <div class="sidebar-header">
    <div class="logo">
      <img src="/console/assets/app_logo.png" alt="EQUOS" class="logo-img" class:collapsed />
      {#if !collapsed}
        <span class="logo-text">EQUOS9</span>
      {/if}
    </div>
    <button class="collapse-btn" onclick={() => collapsed = !collapsed}>
      <svg viewBox="0 0 20 20" fill="currentColor" class:rotated={collapsed}>
        <path fill-rule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clip-rule="evenodd" />
      </svg>
    </button>
  </div>

  <nav class="sidebar-nav">
    {#each navItems as item}
      <button
        class="nav-item"
        class:active={activeNav === item.id}
        onclick={() => handleNavClick(item.id)}
        title={collapsed ? item.label : undefined}
      >
        <span class="nav-icon">
          <NavIcon icon={item.icon} />
        </span>
        {#if !collapsed}
          <span class="nav-label">{item.label}</span>
        {/if}
      </button>
    {/each}
  </nav>

  <div class="sidebar-footer">
    <div class="user-section">
      {#if !collapsed}
        <span class="user-email">{authState.user?.email}</span>
      {/if}
      <button class="logout-btn" onclick={onLogout} title="Sign Out">
        <NavIcon icon="logout" />
        {#if !collapsed}
          <span>Sign Out</span>
        {/if}
      </button>
    </div>
  </div>
</aside>

<style>
  /* Mobile Sidebar Overlay */
  .mobile-sidebar-overlay {
    display: none;
  }

  /* Sidebar Styles */
  .sidebar {
    width: 260px;
    background: linear-gradient(180deg, #1e1e2e 0%, #2d2d44 100%);
    color: white;
    display: flex;
    flex-direction: column;
    transition: width 0.3s ease;
    flex-shrink: 0;
  }

  .sidebar.collapsed {
    width: 72px;
  }

  .sidebar-header {
    padding: 1.5rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  }

  .logo {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  .logo-img {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    transition: width 0.3s ease, height 0.3s ease;
  }

  .logo-img.collapsed {
    width: 36px;
    height: 36px;
  }

  .logo-text {
    font-size: 1.35rem;
    font-weight: 700;
    background: linear-gradient(135deg, #4A7DFF 0%, #6B5CE7 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  .collapse-btn {
    width: 32px;
    height: 32px;
    border: none;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 6px;
    color: white;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.2s;
  }

  .collapse-btn:hover {
    background: rgba(255, 255, 255, 0.2);
  }

  .collapse-btn svg {
    width: 16px;
    height: 16px;
    transition: transform 0.3s;
  }

  .collapse-btn svg.rotated {
    transform: rotate(180deg);
  }

  .sidebar-nav {
    flex: 1;
    padding: 1rem 0.75rem;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  .nav-item {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.75rem 1rem;
    border: none;
    background: transparent;
    color: rgba(255, 255, 255, 0.7);
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s;
    text-align: left;
    width: 100%;
  }

  .sidebar.collapsed .nav-item {
    justify-content: center;
    padding: 0.75rem;
  }

  .nav-item:hover {
    background: rgba(255, 255, 255, 0.1);
    color: white;
  }

  .nav-item.active {
    background: linear-gradient(135deg, #4A7DFF 0%, #6B5CE7 100%);
    color: white;
  }

  .nav-icon {
    width: 20px;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .nav-label {
    font-size: 0.9rem;
    font-weight: 500;
  }

  .sidebar-footer {
    padding: 1rem;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
  }

  .user-section {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .user-email {
    font-size: 0.8rem;
    color: rgba(255, 255, 255, 0.6);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .logout-btn {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 0.75rem;
    border: none;
    background: rgba(255, 255, 255, 0.1);
    color: rgba(255, 255, 255, 0.8);
    border-radius: 6px;
    cursor: pointer;
    font-size: 0.85rem;
    transition: all 0.2s;
  }

  .sidebar.collapsed .logout-btn {
    justify-content: center;
    padding: 0.5rem;
  }

  .logout-btn:hover {
    background: rgba(255, 255, 255, 0.2);
    color: white;
  }

  .logout-btn :global(svg) {
    width: 18px;
    height: 18px;
  }

  /* Responsive */
  @media (max-width: 768px) {
    .mobile-sidebar-overlay {
      display: block;
      position: fixed;
      inset: 0;
      background: rgba(0, 0, 0, 0.5);
      z-index: 40;
      animation: fadeIn 0.2s ease;
    }

    @keyframes fadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }

    .sidebar {
      position: fixed;
      left: 0;
      top: 0;
      bottom: 0;
      width: 280px;
      z-index: 50;
      transform: translateX(-100%);
      transition: transform 0.3s ease;
    }

    .sidebar.collapsed {
      width: 280px;
    }

    .sidebar.mobile-open {
      transform: translateX(0);
    }

    .collapse-btn {
      display: none;
    }
  }
</style>
